import type { ChartPoint } from "../read/readCoverage";

export type StepPathOptions = {
  width: number;
  height: number;
  /** Where the limit sits; the line never rises above it. */
  top: number;
  limit: number;
  /** The span the readings map across, start to today. */
  start: Date;
  end: Date;
  /** The side margins the readings stay inside; the line runs on flat through them. */
  inset?: number;
  reference?: number | null;
  /** From the limit line, the latest reading, and the floor. */
  minReferenceGap?: number;
};

export type StepPaths = {
  line: string;
  strokeLength: number;
  /** The area under the line, for the gradient. */
  fill: string;
  thresholdY: number;
  /** Null when there's no reference, it's $0, or it sits too close to the limit, the latest reading, or the floor. */
  referenceY: number | null;
  /** Where the line ends: the latest reading's height. */
  levelY: number;
  /** One reading has no history to step through: the year before it is unknown. */
  lone: boolean;
};

const round = (n: number) => Math.round(n * 10) / 10;
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/** Readable readings in this plan year, oldest first; last year's belong to its totals. */
const readable = (points: ChartPoint[], start: Date) =>
  points
    .filter(
      p =>
        Number.isFinite(p.met) &&
        Number.isFinite(p.at.getTime()) &&
        p.at >= start,
    )
    .sort((a, b) => a.at.getTime() - b.at.getTime());

const scales = ({
  width,
  height,
  top,
  limit,
  start,
  end,
  inset = 0,
}: StepPathOptions) => {
  const span = end.getTime() - start.getTime();
  return {
    valid: limit > 0 && span > 0,
    x: (t: Date) =>
      round(
        inset +
          clamp01((t.getTime() - start.getTime()) / span) * (width - 2 * inset),
      ),
    y: (met: number) =>
      round(top + (height - top) * (1 - clamp01(met / limit))),
  };
};

/** Steps inside the margins, flat through them at the first and last levels. */
export const stepPath = (
  points: ChartPoint[],
  options: StepPathOptions,
): StepPaths | null => {
  const [first, ...rest] = readable(points, options.start);
  const { valid, x, y } = scales(options);
  if (!first || !valid) {
    return null;
  }
  const last = rest.at(-1) ?? first;
  const steps = rest.map(p => ` H${x(p.at)} V${y(p.met)}`).join("");
  const rise = rest.reduce(
    (sum, p, i) => sum + Math.abs(y(p.met) - y((rest[i - 1] ?? first).met)),
    0,
  );
  const line = `M0 ${y(first.met)}${steps} H${options.width}`;
  const { reference = 0, minReferenceGap = 0, top, height } = options;
  const referenceY = reference && reference > 0 ? y(reference) : null;
  const levelY = y(last.met);
  return {
    line,
    strokeLength: options.width + rise,
    fill: `${line} V${height} H0 Z`,
    thresholdY: top,
    referenceY:
      referenceY !== null &&
      referenceY - top >= minReferenceGap &&
      Math.abs(levelY - referenceY) >= minReferenceGap &&
      height - referenceY >= minReferenceGap
        ? referenceY
        : null,
    levelY,
    lone: rest.length === 0,
  };
};

export type ScrubReading = {
  index: number;
  point: ChartPoint;
  /** Where the knob sits: `x` kept inside the margins, on the step's height. */
  x: number;
  y: number;
};

/** The reading whose step lies under `x`; the first one left of every reading. */
export const readingAt = (
  points: ChartPoint[],
  x: number,
  options: StepPathOptions,
): ScrubReading | null => {
  const { valid, x: xOf, y } = scales(options);
  const inset = options.inset ?? 0;
  const readings = readable(points, options.start);
  const at = Math.max(inset, Math.min(options.width - inset, x));
  let index = Math.max(
    0,
    readings.findLastIndex(p => xOf(p.at) <= at),
  );
  // The API drops readings that didn't move: an equal run is a drawn point.
  while (index > 0 && readings[index]?.met === readings[index - 1]?.met) {
    index -= 1;
  }
  const point = readings[index];
  if (!valid || !point) {
    return null;
  }
  return { index, point, x: at, y: y(point.met) };
};

/** Where a lone reading's tinted unknown year ends and its solid run up to today starts. */
export const loneUnknownUntil = (width: number, inset: number, step: number) =>
  Math.max(0, width - inset - step);

/** Where the line stands at `at`, kept inside the margins. */
export const pointAt = (
  points: ChartPoint[],
  at: Date,
  options: StepPathOptions,
) => readingAt(points, scales(options).x(at), options);
